import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { errMsg } from "@/lib/api";
import { X, Search, MapPin, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { hasGoogle, googleGeocode } from "@/lib/googleMaps";

const pinIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#0020C2;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 3px #0020C255;"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const Recenter = ({ pos }) => {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, Math.max(map.getZoom(), 15)); }, [pos, map]);
  return null;
};

const ClickToPlace = ({ onPlace }) => {
  useMapEvents({ click: (e) => onPlace([e.latlng.lat, e.latlng.lng]) });
  return null;
};

// Adres üzerinden konum bulma + haritada ince ayar (sürükle / tıkla) + kaydetme
export const LocationPickerModal = ({ person, onClose, onSaved }) => {
  const initial = typeof person.lat === "number" && typeof person.lng === "number" ? [person.lat, person.lng] : null;
  const [query, setQuery] = useState([person.address, person.district, person.city].filter(Boolean).join(", "));
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pos, setPos] = useState(initial);
  const [address, setAddress] = useState(person.address || "");
  const [district, setDistrict] = useState(person.district || "");
  const [city, setCity] = useState(person.city || "");
  const [saving, setSaving] = useState(false);

  const search = async () => {
    if (query.trim().length < 3) { toast.error("En az 3 karakter girin"); return; }
    setSearching(true);
    setResults([]);
    // Önce Google (sokak seviyesi hassasiyet), bulunamazsa ücretsiz OSM
    if (hasGoogle()) {
      try {
        const res = await googleGeocode(query.trim());
        if (res.length > 0) {
          setResults(res);
          setPos([res[0].lat, res[0].lng]);
          if (res[0].precision === "ROOFTOP") toast.success("Google: bina seviyesinde nokta atışı konum bulundu");
          setSearching(false);
          return;
        }
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.startsWith("GOOGLE_AUTH")) toast.error(msg.replace("GOOGLE_AUTH: ", ""), { duration: 10000 });
      }
    }
    try {
      const { data } = await api.get("/geocode/search", { params: { q: query.trim() } });
      setResults(data.results);
      if (data.results.length === 0) toast.info("Adres bulunamadı — örnek format: 'Mehmet Akif Mahallesi, Çekmeköy, İstanbul' (kapı no ve daire yazmayın)");
      else {
        setPos([data.results[0].lat, data.results[0].lng]);
        if (data.simplified) toast.info(`Detaylı adres bulunamadı, sadeleştirilmiş arama kullanıldı: "${data.used_query}"`, { duration: 6000 });
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSearching(false); }
  };

  const save = async () => {
    if (!pos) { toast.error("Önce adres arayın veya haritadan konum seçin"); return; }
    setSaving(true);
    try {
      await api.put(`/customer-personnel/${person.id}/location`, {
        lat: pos[0], lng: pos[1], address, district, city,
      });
      toast.success("Konum ve adres güncellendi");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[1200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="location-picker-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Konum Düzelt</div>
            <div className="font-heading font-bold text-lg">{person.first_name} {person.last_name} <span className="text-neutral-400 font-normal text-sm">· {person.company_name}</span></div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="location-picker-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Adres arama */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Adresle Ara</label>
            <div className="flex gap-2 mt-1.5">
              <input
                data-testid="location-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="örn: Yaşar Doğu Mah. Ok Sok. No:12, Etimesgut, Ankara"
                className="flex-1 px-3 py-2 border border-[#E5E5E5] text-sm focus:border-[#0A0A0A] focus:outline-none"
              />
              <button
                data-testid="location-search-btn"
                onClick={search}
                disabled={searching}
                className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50"
              >
                {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Bul
              </button>
            </div>
            <div className="mt-1.5 text-[11px] text-neutral-500 bg-[#FAFAFA] border border-[#F5F5F5] px-2.5 py-1.5" data-testid="location-search-hint">
              <b>Adres formatınız desteklenir:</b> <span className="text-[#0020C2] font-semibold">YEŞİLBAĞLAR MAH. ÇINAR SK. NO:8 İÇ KAPI NO:1 PENDİK/İSTANBUL</span> — kapı no, daire ve parantez içi notlar otomatik temizlenir; bulunamazsa sokak, sonra mahalle seviyesinde aranır.
            </div>
          </div>

          {/* Sonuç adayları */}
          {results.length > 0 && (
            <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5] max-h-[140px] overflow-y-auto" data-testid="location-results">
              {results.map((r, i) => {
                const active = pos && Math.abs(pos[0] - r.lat) < 1e-9 && Math.abs(pos[1] - r.lng) < 1e-9;
                return (
                  <button
                    key={i}
                    data-testid={`location-result-${i}`}
                    onClick={() => setPos([r.lat, r.lng])}
                    className={`w-full flex items-start gap-2 px-3 py-2 text-left text-xs hover:bg-[#F5F8FF] ${active ? "bg-[#F5F8FF] border-l-2 border-l-[#0020C2]" : ""}`}
                  >
                    <MapPin className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${active ? "text-[#0020C2]" : "text-neutral-400"}`} />
                    <span className={active ? "font-semibold" : ""}>{r.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Harita: sürükle veya tıkla */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Haritada İnce Ayar</label>
              <span className="text-[11px] text-neutral-500">Pin&apos;i sürükleyin veya haritaya tıklayın</span>
            </div>
            <div style={{ height: 280 }} className="border border-[#E5E5E5]">
              <MapContainer center={pos || [39.9334, 32.8597]} zoom={pos ? 15 : 6} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Recenter pos={pos} />
                <ClickToPlace onPlace={setPos} />
                {pos && (
                  <Marker
                    position={pos}
                    icon={pinIcon}
                    draggable
                    eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setPos([ll.lat, ll.lng]); } }}
                  />
                )}
              </MapContainer>
            </div>
          </div>

          {/* Adres alanları (düzeltilebilir) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Adres (kayda yazılacak)</label>
              <input data-testid="location-address-input" value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İlçe</label>
              <input data-testid="location-district-input" value={district} onChange={(e) => setDistrict(e.target.value)} className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Şehir</label>
              <input data-testid="location-city-input" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-sm" />
            </div>
            <div className="flex items-end">
              <div className="text-[11px] text-neutral-500 num" data-testid="location-coords">
                {pos ? `${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}` : "Konum seçilmedi"}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
            <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
            <button
              data-testid="location-save-btn"
              onClick={save}
              disabled={saving || !pos}
              className="flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Konumu Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
